import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { interval, Observable, Subscription } from "rxjs";
import { catchError } from "rxjs/operators";
import { BehaviorSubject } from "rxjs";
import _ from "lodash";
import { environment } from "../../../../environments/environment";
import { HelperService } from "../../../services/helper.service";
import { ToastrService } from "ngx-toastr";
import { CookieService } from "ngx-cookie-service";
import { ConfigService } from "../../../services/config.service";
import { EnvService } from "../../../services/env.service";

@Injectable({
	providedIn: "root",
})
export class DownloadService {
	private apiURL = environment.apiBaseUrl;

	// BehaviorSubject to track the download queue
	private _downloadQueueSubject = new BehaviorSubject<
		{
			id: string;
			title: string;
			progress: number;
			status: string;
			jobId: string;
		}[]
	>([]);

	// Observable for components to subscribe to the download queue updates
	downloadQueue$ = this._downloadQueueSubject.asObservable();
	PROCESSING_TIMEOUT = 40 * 60 * 1000;

	constructor(
		private httpClient: HttpClient,
		private helperService: HelperService,
		private toastr: ToastrService,
		private envService: EnvService
	) {
		const timeoutMinutes = this.envService.get("DOWNLOAD_TIMEOUT_MINUTES");
		if (timeoutMinutes !== null) {
			this.PROCESSING_TIMEOUT = timeoutMinutes * 60 * 1000;
		}
	}

	getJobId(chart: {
		id: string;
		title: string;
		url: string;
		params: any;
		is_table_params?: boolean;
		query_params?: any;
	}) {
		const httpOptions = {
			responseType: "blob" as "json",
		};
		let tempParams = _.cloneDeep(chart.params);
		if (chart.is_table_params !== false) {
			tempParams = _.cloneDeep(
				this.helperService.convertToTableListingParams(chart.params)
			);
		}

		if (chart.query_params != null) {
			tempParams = {
				...tempParams,
				...chart.query_params,
			};
		}

		let requestParamsString = this.helperService.convertQueryParams(
			tempParams,
			""
		);
		if (_.get(chart.params, "query_params")) {
			requestParamsString += this.helperService.convertQueryParams(
				_.get(chart.params, "query_params"),
				""
			);
		}

		let finalUrl = `${this.apiURL}/${chart.url}?${requestParamsString}`;

		return this.httpClient
			.get(finalUrl)
			.pipe(catchError(this.helperService.errorHandler.bind(this)));
	}

	startDownload(chart: {
		id: any;
		title: any;
		url: any;
		params: any;
		is_table_params?: boolean;
		query_params?: any;
	}) {
		// Add the new download to the queue
		const existingDownload = this._downloadQueueSubject.value.find(
			(d) =>
				d.id === chart.id &&
				(d.status === "In Progress" ||
					d.status === "Waiting for download" ||
					d.status === "In Queued")
		);

		if (existingDownload) {
			this.toastr.error(
				"The file is already being exported. Please wait until it is completed."
			);
			return;
		}

		this.deleteDownloadById(chart.id);

		const newDownload = {
			id: chart.id,
			title: chart.title,
			progress: 0,
			status: "Waiting for download",
			jobId: "",
		};

		// Push the new download into the queue
		this._downloadQueueSubject.next([
			...this._downloadQueueSubject.value,
			newDownload,
		]);
		this.saveDownloadQueueToLocalStorage();

		this.initiateDownloadProcess(chart);
	}

	private initiateDownloadProcess(chart: {
		id: string;
		title: string;
		url: string;
		params: any;
		is_table_params?: boolean;
		query_params?: any;
	}) {
		const POLLING_DELAY = 3000; // Configurable polling delay in milliseconds
		const RECONNECT_INTERVAL = 60 * 1000; // 1 minute in milliseconds
		let reconnectInterval: ReturnType<typeof setInterval>; // Type for interval reference
		let processingTimeout: ReturnType<typeof setTimeout>; // Type for timeout reference
		let isCompletedOrFailed = false; // Flag to check if the process is completed or failed
		let currentEventSource: EventSource | null = null; // Reference to the current EventSource

		try {
			this.getJobId(chart).subscribe(
				(jobIdResponse: any) => {
					this.updateJobId(chart.id, jobIdResponse.data.job_id);

					const encodedJobId = encodeURIComponent(
						jobIdResponse.data.job_id
					);

					// Use a configurable delay before starting to poll the progress
					setTimeout(() => {
						const pollProgress = () => {
							// Close the current EventSource if it exists
							if (currentEventSource) {
								currentEventSource.close();
							}

							// Start a new EventSource connection
							currentEventSource = new EventSource(
								`${this.apiURL}/export/progress-event/${encodedJobId}`
							);

							// Set processing timeout
							processingTimeout = setTimeout(() => {
								if (!isCompletedOrFailed) {
									// If still processing after 15 minutes, emit a failure
									this.updateDownloadStatus(
										chart.id,
										"Failed"
									);
									clearInterval(reconnectInterval); // Stop the interval
									currentEventSource.close(); // Close the SSE connection
								}
							}, this.PROCESSING_TIMEOUT);

							currentEventSource.onmessage = (event) => {
								const progressResponse = JSON.parse(event.data);
								const download =
									this._downloadQueueSubject.value.find(
										(d) => d.id === chart.id
									);

								if (download) {
									this.updateDownloadProgress(
										chart.id,
										progressResponse.percentage
									);

									if (
										progressResponse.status === "completed"
									) {
										isCompletedOrFailed = true; // Mark as completed
										this.updateDownloadStatus(
											chart.id,
											"Completed"
										);
										download.status = "Completed";
										download.jobId = encodedJobId;
										clearInterval(reconnectInterval); // Stop the interval
										clearTimeout(processingTimeout); // Clear the timeout
										currentEventSource.close(); // Close the SSE connection
									} else if (
										progressResponse.status === "queued"
									) {
										this.updateDownloadStatus(
											chart.id,
											"In Queued"
										);
										download.status = "In Queued";
									} else if (
										progressResponse.status === "processing"
									) {
										this.updateDownloadStatus(
											chart.id,
											"In Progress"
										);
										download.status = "In Progress";
									} else if (
										progressResponse.status === "failed"
									) {
										isCompletedOrFailed = true; // Mark as failed
										this.updateDownloadStatus(
											chart.id,
											"Failed"
										);
										download.status = "Failed";
										download.jobId = null; // Reset jobId if needed
										clearInterval(reconnectInterval); // Stop the interval
										clearTimeout(processingTimeout); // Clear the timeout
										currentEventSource.close(); // Close the SSE connection
									}

									// Emit the updated download queue
									this._downloadQueueSubject.next(
										this._downloadQueueSubject.value
									);
									this.saveDownloadQueueToLocalStorage();
								}
							};

							currentEventSource.onerror = (error) => {
								this.updateDownloadStatus(chart.id, "Failed"); // Ensure status is updated to 'Failed'
								clearInterval(reconnectInterval); // Stop the interval
								clearTimeout(processingTimeout); // Clear the timeout
								currentEventSource.close(); // Close the SSE connection
							};
						};

						// Start polling and set up the reconnect interval
						pollProgress(); // Initial call to start polling
						reconnectInterval = setInterval(() => {
							if (!isCompletedOrFailed) {
								pollProgress(); // Re-poll every minute
							} else {
								clearInterval(reconnectInterval); // Stop the interval if completed or failed
							}
						}, RECONNECT_INTERVAL);
					}, POLLING_DELAY);
				},
				(error) => {
					console.error("Error retrieving job ID:", error); // Log the error
					this.updateDownloadStatus(chart.id, "Failed"); // Update download status to 'Failed'
				}
			);
		} catch (error) {
			console.error("Unexpected error in getJobId:", error); // Log unexpected errors
			this.updateDownloadStatus(chart.id, "Failed"); // Update download status to 'Failed'
		}
	}

	updateJobId(id: string, jobId: string): void {
		const index = this._downloadQueueSubject.value.findIndex(
			(item) =>
				item.id === id &&
				item.status === "Waiting for download" &&
				item.jobId === ""
		);
		if (index !== -1) {
			this._downloadQueueSubject.value[index].jobId = jobId;
			this._downloadQueueSubject.next([
				...this._downloadQueueSubject.value,
			]);
			this.saveDownloadQueueToLocalStorage();
		}
	}

	updateDownloadStatus(id: string, newStatus: string): void {
		const index = this._downloadQueueSubject.value.findIndex(
			(item) =>
				item.id === id &&
				item.status !== "Completed" &&
				item.status !== "Failed"
		);
		if (index !== -1) {
			this._downloadQueueSubject.value[index].status = newStatus;
			this._downloadQueueSubject.next([
				...this._downloadQueueSubject.value,
			]);
			this.saveDownloadQueueToLocalStorage();
		}
	}

	allStatusOnChecking() {
		return this._downloadQueueSubject.value.every(
			(item) => item.status === "Checking"
		);
	}

	updateAllDownloadStatus(newStatus: string): void {
		this._downloadQueueSubject.value.forEach((item) => {
			item.status = newStatus;
		});

		this._downloadQueueSubject.next([...this._downloadQueueSubject.value]);
		this.saveDownloadQueueToLocalStorage();
	}

	updateDownloadProgress(id: string, progress: number): void {
		const index = this._downloadQueueSubject.value.findIndex(
			(item) =>
				item.id === id &&
				item.status !== "Completed" &&
				item.status !== "Failed" &&
				item.progress < 100
		);
		if (index !== -1) {
			const currentItem = this._downloadQueueSubject.value[index];
			if (
				currentItem.status === "In Progress" ||
				currentItem.status === "In Queued"
			) {
				if (
					currentItem.progress === progress ||
					currentItem.progress > progress
				) {
					return;
				}
			}

			currentItem.progress = progress;
			this._downloadQueueSubject.next([
				...this._downloadQueueSubject.value,
			]);
			this.saveDownloadQueueToLocalStorage();
		}
	}

	deleteDownloadById(id: string): void {
		const index = this._downloadQueueSubject.value.findIndex(
			(item) => item.id === id
		);

		if (index !== -1) {
			this._downloadQueueSubject.value.splice(index, 1);
			this._downloadQueueSubject.next([
				...this._downloadQueueSubject.value,
			]);
			this.saveDownloadQueueToLocalStorage();
		}
	}

	deleteAll() {
		this._downloadQueueSubject.next([]);
		this.deleteDownloadQueueFromLocalStorage();
	}

	async loadDownloadQueueFromLocalStorage(): Promise<void> {
		const storedQueue = localStorage.getItem("downloadQueue");
		const storedTimestamp = localStorage.getItem("downloadQueueTimestamp");

		if (storedQueue && storedTimestamp) {
			const now = Date.now();
			const oneHour = 60 * 60 * 1000;
			const timestamp = parseInt(storedTimestamp, 10);

			if (now - timestamp > oneHour) {
				localStorage.removeItem("downloadQueue");
				localStorage.removeItem("downloadQueueTimestamp");
				this._downloadQueueSubject.next([]); // Kosongkan antrian
			} else {
				const downloadQueue = JSON.parse(storedQueue);
				this._downloadQueueSubject.next(JSON.parse(storedQueue));
				this.updateAllDownloadStatus("Checking");

				await downloadQueue.forEach(
					(item: {
						id: string;
						title: string;
						progress: number;
						status: string;
						jobId: string;
					}) => {
						const POLLING_DELAY = 3000; // Configurable polling delay in milliseconds
						const RECONNECT_INTERVAL = 60 * 1000; // 1 minute in milliseconds
						let reconnectInterval: ReturnType<typeof setInterval>; // Type for interval reference
						let processingTimeout: ReturnType<typeof setTimeout>; // Type for timeout reference
						let isCompletedOrFailed = false; // Flag to check if the process is completed or failed
						let currentEventSource: EventSource | null = null; // Reference to the current EventSource

						if (item.status === "Completed") {
							this.deleteDownloadById(item.id);
						}

						// Use a configurable delay before starting to poll the progress
						setTimeout(() => {
							const pollProgress = () => {
								const encodedJobId = encodeURIComponent(
									item.jobId
								);

								// Close the current EventSource if it exists
								if (currentEventSource) {
									currentEventSource.close();
								}

								// Start a new EventSource connection
								currentEventSource = new EventSource(
									`${this.apiURL}/export/progress-event/${encodedJobId}`
								);

								// Set processing timeout
								processingTimeout = setTimeout(() => {
									if (!isCompletedOrFailed) {
										// If still processing after 15 minutes, emit a failure
										this.updateDownloadStatus(
											item.id,
											"Failed"
										);
										clearInterval(reconnectInterval); // Stop the interval
										currentEventSource.close(); // Close the SSE connection
									}
								}, this.PROCESSING_TIMEOUT);

								currentEventSource.onmessage = (event) => {
									const progressResponse = JSON.parse(
										event.data
									);
									const download =
										this._downloadQueueSubject.value.find(
											(d) => d.id === item.id
										);

									if (download) {
										this.updateDownloadProgress(
											item.id,
											progressResponse.percentage
										);

										if (
											progressResponse.status ===
											"completed"
										) {
											isCompletedOrFailed = true; // Mark as completed
											this.updateDownloadStatus(
												item.id,
												"Completed"
											);
											download.status = "Completed";
											download.jobId = encodedJobId;
											clearInterval(reconnectInterval); // Stop the interval
											clearTimeout(processingTimeout); // Clear the timeout
											currentEventSource.close(); // Close the SSE connection
										} else if (
											progressResponse.status === "queued"
										) {
											this.updateDownloadStatus(
												item.id,
												"In Queued"
											);
											download.status = "In Queued";
										} else if (
											progressResponse.status ===
											"processing"
										) {
											this.updateDownloadStatus(
												item.id,
												"In Progress"
											);
											download.status = "In Progress";
										} else if (
											progressResponse.status === "failed"
										) {
											isCompletedOrFailed = true; // Mark as failed
											this.updateDownloadStatus(
												item.id,
												"Failed"
											);
											download.status = "Failed";
											download.jobId = null; // Reset jobId if needed
											clearInterval(reconnectInterval); // Stop the interval
											clearTimeout(processingTimeout); // Clear the timeout
											currentEventSource.close(); // Close the SSE connection
										}

										// Emit the updated download queue
										this._downloadQueueSubject.next(
											this._downloadQueueSubject.value
										);
										this.saveDownloadQueueToLocalStorage();
									}
								};

								currentEventSource.onerror = (error) => {
									this.updateDownloadStatus(
										item.id,
										"Failed"
									); // Ensure status is updated to 'Failed'
									clearInterval(reconnectInterval); // Stop the interval
									clearTimeout(processingTimeout); // Clear the timeout
									currentEventSource.close(); // Close the SSE connection
								};

								// this._showDownloadFloatingSubject.next(true);
							};

							// Start polling and set up the reconnect interval
							pollProgress(); // Initial call to start polling
							reconnectInterval = setInterval(() => {
								if (!isCompletedOrFailed) {
									pollProgress(); // Re-poll every minute
								} else {
									clearInterval(reconnectInterval); // Stop the interval if completed or failed
								}
							}, RECONNECT_INTERVAL);
						}, POLLING_DELAY);
					}
				);
			}
		}
	}

	saveDownloadQueueToLocalStorage(): void {
		const downloadQueue = this._downloadQueueSubject.value;
		localStorage.setItem("downloadQueue", JSON.stringify(downloadQueue));
		localStorage.setItem("downloadQueueTimestamp", Date.now().toString());
	}

	deleteDownloadQueueFromLocalStorage(): void {
		localStorage.removeItem("downloadQueue");
		localStorage.removeItem("downloadQueueTimestamp");
	}
}
